/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import axios from 'axios';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { useCompanyStore } from './UserService/useCompanyStore';
export const useTrustAccountingStore = defineStore('trustAccountingStore', {
  state: () => {
    return {
      saving: false,
      vendors: [],
      loading: false,
      connected: false,
      configured: false,
      accounts: [],
      settings: {},
      liabilityTracking: true,
      accountingState: {},
      accountingAccounts: {
        trustBank: [],
        trustLiability: [],
        operating: [],
        undeposited: []
      },
      confidoTrustAccounts: []
    };
  },
  actions: {
    async loadAccounts() {
      try {
        const response = await axios({
          url: `/accounting/trustSettings`
        });
        this.$patch({
          accounts: response.data
        });
        return response.data;
      } catch (error) {
        useSnackbarHandler().handleError(error?.response?.data?.message || error.message);
      }
    },
    async loadConfidoTrustAccounts() {
      try {
        const response = await axios({
          url: `/confido/trustAccounts`
        });
        this.$patch({
          confidoTrustAccounts: response.data
        });
        return response.data;
      } catch (error) {
        useSnackbarHandler().handleError(error?.response?.data?.message || error.message);
      }
    },
    async accountingListVendors() {
      try {
        const response = await axios({
          url: `/accounting/vendors`,
          method: 'GET'
        });
        this.$patch({
          vendors: response.data.vendors
        });
        return response.data.vendors;
      } catch (error) {
        useSnackbarHandler().handleError(`Unable to fetch QBO vendors: ${error?.response?.data?.message || error.message}`);
      }
    },
    async createVendor({
      displayName
    }) {
      const response = await axios.post('/accounting/vendors', {
        displayName
      });
      const vendor = response.data;
      this.vendors.push(vendor);
      return vendor;
    },
    async listAccountingAccounts({
      excludeTrust = false
    } = {}) {
      try {
        const response = await axios({
          url: `/accounting/accounts${excludeTrust ? '?excludeTrust=true' : ''}`,
          method: 'GET'
        });

        // Process accounts data
        if (response.data) {
          const accounts = response.data;
          accounts.trustBank = accounts.bank.filter(a => a.subtype === 'TrustAccounts');
          accounts.trustLiability = accounts.liability.filter(a => a.subtype === 'TrustAccountsLiabilities');
          accounts.operating = accounts.bank.filter(a => a.subtype !== 'TrustAccounts');
          accounts.undeposited = accounts.asset.filter(a => a.subtype === 'UndepositedFunds');
          this.$patch({
            accountingAccounts: accounts
          });
        }
        return response.data;
      } catch (error) {
        useSnackbarHandler().handleError(error?.response?.data?.message || error.message);
      }
    },
    async createAccountingAccount(accountData) {
      try {
        const response = await axios.post('/accounting/accounts', accountData);
        return response.data;
      } catch (error) {
        useSnackbarHandler().handleError(error?.response?.data?.message || error.message);
      }
    },
    async accountingCreateFirmVendor() {
      try {
        const response = await axios.post('/accounting/vendors');
        this.$patch({
          settings: {
            paymentVendor: response.data
          }
        });
        return response.data;
      } catch (error) {
        useSnackbarHandler().handleError(error?.response?.data?.message || error.message);
      }
    },
    async load(force, me) {
      if (!this.loading || force) {
        this.loading = true;
        try {
          await this.loadSettings(me.company?.settings?.trust, me.company?.settings?.isTrustMemoEnabled);
          const statusResponse = await this.verify(true, me);
          const accountsResponse = await this.loadAccounts();
          const accountingAccountsResponse = await this.listAccountingAccounts();
          if (accountingAccountsResponse) {
            this.accountingAccounts = accountingAccountsResponse;
          }
          this.connected = statusResponse.connected;
          this.accounts = accountsResponse;
          this.settings.accounts = this.accounts;
          this.loading = false;
        } catch (error) {
          this.connected = false;
        } finally {
          this.loading = false;
        }
      }
    },
    async loadSettings(trustSettings, isTrustMemoEnabled) {
      this.configured = !!trustSettings;
      this.settings = trustSettings || {};
      this.settings.isTrustMemoEnabled = isTrustMemoEnabled;
      this.liabilityTracking = typeof this.settings.liabilityTracking === 'undefined' ? true : this.settings.liabilityTracking;
      this.settings.liabilityTracking = this.liabilityTracking;
    },
    async verify(prompt, me) {
      try {
        if (!me.company.integration.quickbooks) {
          useSnackbarHandler().handleError('QuickBooks integration not enabled');
        }
        const status = await axios.get('/accounting');
        if (!status.data.connected) {
          if (prompt) {
            useSnackbarHandler().handleError('You are disconnected from QuickBooks. Go to Settings to reconnect...');
          } else {
            useSnackbarHandler().handleError('You are disconnected from QuickBooks');
          }
        }
        this.accountingState.connected = status.data.connected;
        return status.data;
      } catch (error) {
        useSnackbarHandler().handleError(error?.response?.data?.message || error.message);
      }
    },
    async update() {
      const {
        trustConfig
      } = this.settings;
      if (trustConfig && this.configured) {
        trustConfig.memoPattern = trustConfig.memoPattern.replace(/\n?$/, '\n');
      } else {
        this.settings.trustConfig = {};
      }
      try {
        const companyStore = useCompanyStore();
        const updatedCompany = await companyStore.updateMyCompany({
          settings: {
            trust: this.settings ? this.settings : {},
            isTrustMemoEnabled: this.settings.isTrustMemoEnabled
          }
        });
        this.loadSettings(updatedCompany?.settings?.trust, updatedCompany?.settings?.isTrustMemoEnabled);
        return updatedCompany;
      } catch (error) {
        useSnackbarHandler().handleError(error?.response?.data?.message || error.message);
      }
    },
    async trustCheck(trustId) {
      try {
        const response = await axios.get(`/me/company/trusts/${trustId}`);
        return response.data;
      } catch (error) {
        useSnackbarHandler().handleError(error?.response?.data?.message || error.message);
      }
    },
    async trustDisconnect(trustId) {
      try {
        return await axios.post(`/me/company/trusts/${trustId}/disconnect`);
      } catch (error) {
        useSnackbarHandler().handleError(error?.response?.data?.message || error.message);
      }
    },
    trustFromId(id) {
      return this.accounts.find(a => a.id === id);
    },
    async updateSettings(operating, deposits, config, evergreen, evergreenService, printLater, vendor, automate, isTrustMemoEnabled) {
      this.settings.accounts = this.accounts;
      this.settings.operating = operating || {};
      if (deposits && Object.keys(deposits).length > 0) {
        this.settings.deposits = deposits;
      } else {
        delete this.settings.deposits;
      }
      this.settings.trustConfig = config || {};
      this.settings.evergreen = evergreen || false;
      this.settings.evergreenService = evergreenService || {};
      this.settings.printLater = printLater || false;
      this.settings.paymentVendor = vendor || {};
      this.settings.automate = automate || false;
      this.settings.isTrustMemoEnabled = isTrustMemoEnabled || false;
      return await this.update();
    },
    async addTrust(trust) {
      this.settings.accounts = this.settings.accounts || [];
      this.settings.accounts.push(trust);
      return await this.update();
    },
    async deleteTrust(trust) {
      this.settings.accounts = this.settings.accounts.filter(a => a.id !== trust.id);
      if (this.settings.accounts.length === 0) {
        this.settings = {};
      }
      return await this.update();
    },
    async updateTrust(trust) {
      const found = this.settings.accounts.find(a => a.id === trust.id);
      if (found) {
        if (trust.defaultAccount === found.defaultAccount) {
          if (trust.defaultAccount) {
            this.settings.accounts.forEach(a => {
              if (a.id !== trust.id) {
                a.defaultAccount = false;
              }
            });
          } else {
            const notThis = this.settings.accounts.find(a => a.id !== trust.id);
            if (notThis) notThis.defaultAccount = true;
          }
        }
        const updatedAccount = {
          ...found,
          ...trust
        };
        const index = this.settings.accounts.findIndex(a => a.id === trust.id);
        if (index !== -1) this.settings.accounts[index] = updatedAccount;
        return await this.update();
      }
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    },
    trustMemo({
      trustBalance,
      invoiceBalance,
      accountBalance,
      evergreen,
      config
    }) {
      const due = invoiceBalance + (accountBalance || 0);
      const replace = (pattern, accountAfter, trustAfter, trustPayment, evergreenAmount, paymentRequired, replenish) => {
        if (!pattern) return '';
        return pattern.replace('{InvoiceAmount}', this.formatCurrency(invoiceBalance)).replace('{AmountDue}', this.formatCurrency(due)).replace('{AccountBefore}', this.formatCurrency(accountBalance)).replace('{AccountAfter}', this.formatCurrency(accountAfter)).replace('{TrustBefore}', this.formatCurrency(trustBalance)).replace('{TrustAfter}', this.formatCurrency(trustAfter)).replace('{TrustPayment}', this.formatCurrency(trustPayment)).replace('{TrustAfterSimple}', this.formatCurrency(invoiceBalance > trustBalance ? 0 : trustBalance - invoiceBalance)).replace('{TrustPaymentSimple}', this.formatCurrency(invoiceBalance > trustBalance ? trustBalance : invoiceBalance)).replace('{TrustEvergreen}', this.formatCurrency(evergreenAmount)).replace('{TrustEvergreenReplenish}', this.formatCurrency(replenish)).replace('{PaymentRequired}', this.formatCurrency(paymentRequired));
      };
      const replaceLegacy = (pattern, payment, after) => {
        if (!pattern) return '';
        return pattern.replace('{before}', this.formatCurrency(trustBalance)).replace('{payment}', this.formatCurrency(payment)).replace('{after}', this.formatCurrency(after)).replace('{invoice}', this.formatCurrency(invoiceBalance));
      };
      if (config.memoPattern.indexOf('{before}') > 0 || config.memoPattern.indexOf('{after}') > 0) {
        if (trustBalance >= invoiceBalance) {
          return replaceLegacy(config.memoPatternPartial, invoiceBalance, trustBalance - invoiceBalance);
        }
        return replaceLegacy(config.memoPattern, trustBalance, invoiceBalance - trustBalance);
      }
      if (evergreen) {
        if (trustBalance >= due) {
          const trustAfter = trustBalance - due;
          const replenish = trustAfter < evergreen ? evergreen - trustAfter : 0;
          return replace(config.memoPatternEvergreen, 0, trustAfter, due, evergreen, replenish, replenish);
        }
        const accountAfter = due - trustBalance;
        return replace(config.memoPatternEvergreen, accountAfter, 0, trustBalance, evergreen, evergreen + accountAfter, evergreen);
      }
      if (trustBalance >= due) {
        return replace(config.memoPatternPartial, 0, trustBalance - due, due);
      }
      return replace(config.memoPattern, due - trustBalance, 0, trustBalance);
    }
  }
});